<script setup lang="ts">
import { NuxtLinkLocale, VFragment } from '#components';
import ChevronIcon from '~/components/icons/ChevronIcon.vue';

export interface BaseButtonProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  type?: 'button' | 'icon' | 'link';
  variant?:
    | 'primary'
    | 'secondary'
    | 'outlined'
    | 'text'
    | 'ghost'
    | 'glass'
    | 'blue';
  inverted?: boolean;
  roundCorners?: 'all' | 'top' | 'bottom';
  block?: boolean;
  disabled?: boolean;
  customSeoTag?: string;
  url?: string;
  element?: 'anchor' | 'button';
  targetBlank?: boolean;
}

const props = withDefaults(defineProps<BaseButtonProps>(), {
  size: 'md',
  type: 'button',
  variant: 'primary',
  inverted: false,
  roundCorners: 'all',
  block: false,
  disabled: false,
  customSeoTag: 'strong',
  url: '',
  element: 'button',
  targetBlank: false,
});

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();

const isAnchor = computed(() => props.element === 'anchor');

const componentAttrs = computed(() => {
  return isAnchor.value
    ? {
        to: props.url,
        target: props.targetBlank ? '_blank' : null,
        ...attrs,
        class: '',
      }
    : { type: 'button', disabled: props.disabled, ...attrs };
});

const buttonClasses = computed(() => {
  return {
    'h-17 px-6 md:h-14': props.size === 'lg' && props.variant !== 'text',

    'h-11 px-6 md:h-12': props.size === 'md' && props.variant !== 'text',

    'h-8 px-3': props.size === 'sm' && props.variant !== 'text',

    'h-7 px-1':
      (props.size === 'lg' || props.size === 'md') && props.variant === 'text',

    'h-6 px-0.5': props.size === 'sm' && props.variant === 'text',
  };
});

const iconClasses = computed(() => {
  return {
    'h-17 px-4.5 md:h-14 md:px-4': props.size === 'lg',

    'h-11 px-3 md:h-12 md:px-3.5': props.size === 'md',

    'h-10 px-2.5': props.size === 'sm',

    'h-8 px-1.5': props.size === 'xs',
  };
});

const typeClasses = computed(() => {
  switch (props.type) {
    case 'icon':
      return iconClasses.value;
    case 'link':
      return {};
    default:
      return buttonClasses.value;
  }
});

const classes = computed(() => {
  return {
    'box-border flex cursor-pointer items-center justify-center disabled:pointer-events-none':
      true,
    ...typeClasses.value,
  };
});

const { font: linkFont } = useTextSize(props.size);

const textClasses = computed(() => {
  return {
    'flex items-end justify-center text-nowrap transition-colors ease-out':
      true,

    'text-b2 uppercase': props.type === 'button',

    'align-bottom font-normal hover:underline': props.type === 'link',
    [linkFont.value || '']: props.type === 'link',
  };
});

const roundedClassesPostfix = computed(() => {
  return props.variant === 'outlined' ? '-outline' : '';
});

const iconRoundedClasses = computed(() => {
  return {
    [`rounded-2xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'lg' && props.roundCorners === 'all',
    [`rounded-xl-smooth${roundedClassesPostfix.value}`]:
      (props.size === 'md' || props.size === 'sm') &&
      props.roundCorners === 'all',
    [`rounded-10px-smooth${roundedClassesPostfix.value}`]:
      props.size === 'xs' && props.roundCorners === 'all',

    [`rounded-t-2xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'lg' && props.roundCorners === 'top',
    [`rounded-t-xl-smooth${roundedClassesPostfix.value}`]:
      (props.size === 'md' || props.size === 'sm') &&
      props.roundCorners === 'top',
    [`rounded-t-10px-smooth${roundedClassesPostfix.value}`]:
      props.size === 'xs' && props.roundCorners === 'top',

    [`rounded-b-2xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'lg' && props.roundCorners === 'bottom',
    [`rounded-b-xl-smooth${roundedClassesPostfix.value}`]:
      (props.size === 'md' || props.size === 'sm') &&
      props.roundCorners === 'bottom',
    [`rounded-b-10px-smooth${roundedClassesPostfix.value}`]:
      props.size === 'xs' && props.roundCorners === 'bottom',
  };
});

const buttonRoundedClasses = computed(() => {
  return {
    [`rounded-2xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'lg' && props.roundCorners === 'all',
    [`rounded-xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'md' && props.roundCorners === 'all',
    [`rounded-lg-smooth${roundedClassesPostfix.value}`]:
      props.size === 'sm' && props.roundCorners === 'all',

    [`rounded-t-2xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'lg' && props.roundCorners === 'top',
    [`rounded-t-xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'md' && props.roundCorners === 'top',
    [`rounded-t-lg-smooth${roundedClassesPostfix.value}`]:
      props.size === 'sm' && props.roundCorners === 'top',

    [`rounded-b-2xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'lg' && props.roundCorners === 'bottom',
    [`rounded-b-xl-smooth${roundedClassesPostfix.value}`]:
      props.size === 'md' && props.roundCorners === 'bottom',
    [`rounded-b-lg-smooth${roundedClassesPostfix.value}`]:
      props.size === 'sm' && props.roundCorners === 'bottom',
  };
});

const typeRoundedClasses = computed(() => {
  switch (props.type) {
    case 'icon':
      return iconRoundedClasses.value;
    default:
      return buttonRoundedClasses.value;
  }
});

const defaultVariantColorsClasses = computed(() => {
  return {
    'text-sea-wave-500 disabled:text-grey-400':
      ['primary', 'glass', 'ghost', 'text', 'outline'].includes(
        props.variant,
      ) && props.type === 'link',

    'text-dark-blue disabled:text-grey-400':
      props.variant === 'secondary' && props.type === 'link',

    'bg-dark-blue text-white hover:text-sea-wave focus-visible:text-sea-wave active:bg-dark-blue-pressed disabled:bg-grey-400 disabled:text-white-70':
      props.variant === 'primary' && props.type !== 'link',

    'bg-grey-50 text-grey-1000 hover:bg-grey-200 focus-visible:bg-grey-200 active:bg-grey-300 disabled:bg-grey-100 disabled:text-grey-400':
      props.variant === 'secondary' && props.type !== 'link',

    'border-dark-blue text-dark-blue after:bg-dark-blue hover:bg-dark-blue-5 focus-visible:bg-dark-blue-5 active:bg-dark-blue-10 disabled:border-grey-400 disabled:text-grey-400 disabled:after:bg-grey-400':
      props.variant === 'outlined' && props.type !== 'link',

    'rounded-md text-dark-blue hover:text-dark-blue-50 focus-visible:text-dark-blue-50 active:text-dark-blue disabled:text-grey-400':
      (props.variant === 'text' || props.variant === 'ghost') &&
      props.type !== 'link',

    'bg-white-10 text-dark-blue backdrop-blur-sm hover:bg-white-30 focus-visible:bg-white-30 active:bg-white-70 disabled:text-grey-400':
      props.variant === 'glass',

    'bg-sea-wave-400 text-dark-blue hover:bg-sea-wave-300 focus-visible:bg-sea-wave-300 active:bg-sea-wave-300 disabled:text-dark-blue-20':
      props.variant === 'blue',
  };
});

const invertedVariantColorsClasses = computed(() => {
  return {
    'text-sea-wave-500 disabled:text-grey-400': props.type === 'link',

    'bg-white text-dark-blue hover:text-sea-wave focus-visible:text-sea-wave active:bg-grey-50 disabled:bg-grey-400 disabled:text-dark-blue-50':
      props.variant === 'primary' && props.type !== 'link',

    'border-white text-white after:bg-white hover:bg-white-5 focus-visible:bg-white-5 active:bg-white-10 disabled:border-grey-800 disabled:text-grey-800 disabled:after:bg-grey-800':
      props.variant === 'outlined' && props.type !== 'link',

    'text-white hover:text-white-70 focus-visible:text-white-70 active:text-white disabled:text-grey-800':
      (props.variant === 'text' || props.variant === 'ghost') &&
      props.type !== 'link',
  };
});

const uiClasses = computed(() => {
  return {
    'transition-colors ease-out': true,

    'w-full': props.block,
    'w-min': !props.block,

    ...(['ghost', 'text'].includes(props.variant)
      ? {}
      : typeRoundedClasses.value),

    ...(props.inverted
      ? invertedVariantColorsClasses.value
      : defaultVariantColorsClasses.value),
  };
});
</script>

<template>
  <!-- 
    Applies squircle CSS Paint to wrapper around the link because CSS Paint API
    cannot be applied to anchor elements directly.

    https://github.com/w3c/css-houdini-drafts/issues/791 
  -->
  <component
    :is="isAnchor ? 'div' : VFragment"
    :class="[uiClasses, attrs.class]"
  >
    <component
      :is="isAnchor ? NuxtLinkLocale : 'button'"
      v-bind="componentAttrs"
      :class="[classes, isAnchor ? null : uiClasses]"
    >
      <component :is="customSeoTag" :class="textClasses">
        <slot />

        <ChevronIcon v-if="type === 'link'" class="h-[1em] w-[1em]" />
      </component>
    </component>
  </component>
</template>
